import { Fragment, useEffect, useMemo, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DatePicker } from '../components/ui/date-picker';
import BankCombobox from '../components/BankCombobox';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tip } from '../components/ui/tip';
import { hoyLocal } from '../utils/fechaLocal';

/**
 * Ctas. Corrientes → Registrar pago (página).
 * Con método "Cheque" el pago se puede dividir en varios cheques con distintas
 * fechas de cobro: se indica total + cantidad, se precarga la tabla y cada
 * cheque es editable (banco, número, monto, fecha). El movimiento de la cta cte
 * se registra por la suma y cada cheque entra a la cartera (Finanzas → Cheques).
 */

const fmtARS = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 }).format(n || 0);

interface Cta { id: string; displayName: string; entityType: string; currentBalance: number; entityId?: string; }
interface ChequeRow { checkNumber: string; bank: string; amount: string; issueDate: string; dueDate: string; }

const hoy = () => hoyLocal();
const normTxt = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Input de plata con formato es-AR mientras se escribe (856.243,00).
// El estado guarda el número crudo ('856243.45'); el input muestra/parsea con puntos y coma.
const fmtMonto = (raw: string) => {
  if (raw === '' || raw == null) return '';
  const [i, d] = String(raw).split('.');
  const int = (i || '').replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return d !== undefined ? `${int || '0'},${d}` : int;
};
const parseMonto = (txt: string) => {
  const clean = txt.replace(/[^\d.,]/g, '').replace(/\./g, '');
  const parts = clean.split(',');
  const int = parts[0] || '';
  const dec = parts.length > 1 ? parts.slice(1).join('').slice(0, 2) : undefined;
  if (int === '' && dec === undefined) return '';
  return dec !== undefined ? `${int || '0'}.${dec}` : int;
};
function MoneyInput({ value, onChange, className, placeholder = '0,00' }: { value: string; onChange: (raw: string) => void; className?: string; placeholder?: string }) {
  return (
    <div className="relative">
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">$</span>
      <Input inputMode="decimal" value={fmtMonto(value)} onChange={e => onChange(parseMonto(e.target.value))} className={`pl-6 ${className || ''}`} placeholder={placeholder} />
    </div>
  );
}

// Combobox de cuentas corrientes con filtro al escribir (nombre del cliente/proveedor)
function CtaCombobox({ accounts, value, onChange }: { accounts: Cta[]; value: string; onChange: (id: string) => void }) {
  const [query, setQuery] = useState('');
  const selected = accounts.find(a => a.id === value) || null;
  const label = (a: Cta) => `${a.displayName} — ${fmtARS(a.currentBalance)}${a.entityType === 'provider' ? ' (prov.)' : ''}`;
  const filtered = useMemo(() => {
    const q = normTxt(query.trim());
    if (!q) return accounts;
    return accounts.filter(a => normTxt(a.displayName).includes(q));
  }, [accounts, query]);
  return (
    <Combobox value={selected} onChange={(a: Cta | null) => onChange(a?.id || '')} by="id">
      <div className="relative">
        <div className="relative">
          <Combobox.Input
            className="w-full h-9 px-3 pr-8 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Buscar cuenta..."
            displayValue={(a: Cta | null) => (a ? label(a) : '')}
            onChange={e => setQuery(e.target.value)}
          />
          <Combobox.Button className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <ChevronUpDownIcon className="h-4 w-4" aria-hidden="true" />
          </Combobox.Button>
        </div>
        <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg shadow-lg border bg-card">
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-center text-sm text-muted-foreground">Sin resultados</div>
          )}
          {filtered.map(a => (
            <Combobox.Option key={a.id} value={a} as={Fragment}>
              {({ active, selected: sel }) => (
                <li className={`relative cursor-pointer select-none px-3 py-1.5 text-sm flex items-center justify-between gap-2 ${active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>
                  <span className={sel ? 'font-semibold' : 'font-normal'}>{label(a)}</span>
                  {sel && <CheckIcon className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />}
                </li>
              )}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}

export default function CurrentAccountPay() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<Cta[]>([]);
  const [methods, setMethods] = useState<{ code: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);

  // Edición de un pago existente: se reabre este mismo editor precargado y al
  // guardar se reemplaza (se registra el pago corregido y se borra el viejo con
  // sus cheques). Asi la correccion pasa por la misma logica que el alta.
  const editMovementId = searchParams.get('movementId') || '';
  const esEdicion = Boolean(editMovementId);
  const [loadingMovement, setLoadingMovement] = useState(esEdicion);
  const [chequesBloqueados, setChequesBloqueados] = useState<string[]>([]);

  const [accountId, setAccountId] = useState(searchParams.get('accountId') || '');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [payDate, setPayDate] = useState(hoy());
  const [description, setDescription] = useState('');

  // Compra del proveedor a la que se vincula el pago (opcional, trazabilidad)
  const [purchases, setPurchases] = useState<{ id: string; purchaseNumber: string; total: number; issueDate?: string }[]>([]);
  const [linkedPurchase, setLinkedPurchase] = useState('');

  // Cheques
  const [chequeCount, setChequeCount] = useState('1');
  const [cheques, setCheques] = useState<ChequeRow[]>([]);
  // Cheques EN CARTERA (recibidos de clientes) para endosar al proveedor en
  // este mismo pago: se listan los pendientes y se tildan los que van
  const [carteraDisp, setCarteraDisp] = useState<any[]>([]);
  const [carteraSel, setCarteraSel] = useState<string[]>([]);

  // Retenciones que hizo el cliente al pagar (también cancelan deuda)
  const RETENCIONES: Array<{ tipo: 'ganancias' | 'iibb' | 'sellos'; label: string }> = [
    { tipo: 'ganancias', label: 'Ganancias' },
    { tipo: 'iibb', label: 'Ingresos Brutos' },
    { tipo: 'sellos', label: 'Imp. a los Sellos' },
  ];
  const [retenciones, setRetenciones] = useState<Record<string, { amount: string; certificado: string }>>({
    ganancias: { amount: '', certificado: '' },
    iibb: { amount: '', certificado: '' },
    sellos: { amount: '', certificado: '' },
  });
  const setRetencion = (tipo: string, patch: Partial<{ amount: string; certificado: string }>) =>
    setRetenciones(prev => ({ ...prev, [tipo]: { ...prev[tipo], ...patch } }));
  const retencionesTotal = useMemo(
    () => Object.values(retenciones).reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [retenciones]
  );

  useEffect(() => {
    axios.get('/api/current-accounts', { params: { limit: 500 } })
      .then(r => setAccounts(r.data.accounts || []))
      .catch(() => toast.error('Error al cargar las cuentas'));
    axios.get('/api/settings/payment-methods')
      .then(r => setMethods((Array.isArray(r.data) ? r.data : []).filter((m: any) => m.code !== 'cuenta_corriente')))
      .catch(() => {});
  }, []);

  // Precarga del pago que se está corrigiendo
  useEffect(() => {
    if (!esEdicion || !accountId) return;
    let cancelado = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/current-accounts/${accountId}/movements/${editMovementId}`);
        if (cancelado) return;
        const mov = data.movement || {};
        const checks: any[] = data.checks || [];
        setMethod(mov.paymentMethod || '');
        // Se muestra la fecha tal como quedó guardada.
        setPayDate(String(mov.createdAt || '').slice(0, 10) || hoy());
        setDescription(mov.description || '');
        if (checks.length) {
          setChequeCount(String(checks.length));
          setCheques(checks.map((c) => ({
            checkNumber: c.checkNumber || '',
            bank: c.bank || '',
            amount: String(c.amount ?? ''),
            issueDate: String(c.issueDate || '').slice(0, 10),
            dueDate: String(c.dueDate || '').slice(0, 10),
          })));
          setAmount(String(checks.reduce((s, c) => s + (Number(c.amount) || 0), 0)));
          // Un cheque ya depositado/acreditado no se puede regenerar: si se
          // reemplaza el pago quedaría duplicado en Cheques.
          setChequesBloqueados(checks.filter((c) => c.status && c.status !== 'pending')
            .map((c) => `${c.checkNumber || 's/n'} (${c.status})`));
        } else {
          setAmount(String(mov.amount ?? ''));
        }
      } catch (err: any) {
        toast.error(err.response?.data?.error || 'No se pudo cargar el pago');
      } finally {
        if (!cancelado) setLoadingMovement(false);
      }
    })();
    return () => { cancelado = true; };
  }, [esEdicion, editMovementId, accountId]);

  const account = useMemo(() => accounts.find(a => a.id === accountId), [accounts, accountId]);

  // Si la cuenta es de un proveedor, traer sus compras para el vínculo opcional
  useEffect(() => {
    setLinkedPurchase('');
    if (account?.entityType === 'provider' && account?.entityId) {
      axios.get('/api/purchases', { params: { providerId: account.entityId } })
        .then(r => setPurchases(r.data.purchases || r.data || []))
        .catch(() => setPurchases([]));
    } else {
      setPurchases([]);
    }
  }, [account?.entityId, account?.entityType]);
  const esCheque = method === 'cheque';
  const sumaCheques = useMemo(() => cheques.reduce((s, c) => s + (Number(c.amount) || 0), 0), [cheques]);

  // Cartera disponible: solo para pagos a PROVEEDORES con cheque
  const esProveedor = account?.entityType === 'provider';
  useEffect(() => {
    setCarteraSel([]);
    if (!(esCheque && esProveedor)) { setCarteraDisp([]); return; }
    axios.get('/api/checks', { params: { type: 'received', status: 'pending' } })
      .then(r => {
        const lista = (r.data?.checks || r.data || []).filter((c: any) => c.type === 'received' && c.status === 'pending');
        lista.sort((a: any, b: any) => String(a.dueDate || '').localeCompare(String(b.dueDate || '')));
        setCarteraDisp(lista);
      })
      .catch(() => setCarteraDisp([]));
  }, [esCheque, esProveedor]);
  const sumaCartera = useMemo(
    () => carteraDisp.filter(c => carteraSel.includes(c.id)).reduce((s, c) => s + (Number(c.amount) || 0), 0),
    [carteraDisp, carteraSel]
  );

  // La fecha del pago es CUÁNDO SE PAGÓ: por defecto hoy, o la que se ponga a
  // mano si se está cargando un pago de otro día. No tiene que ver con las
  // fechas de los cheques (ni la emisión ni el vencimiento).
  const pagoEnElFuturo = Boolean(payDate && payDate > hoy());
  const totalDeclarado = Number(amount) || 0;
  // El monto es el TOTAL que cancela; las retenciones son parte de ese total.
  // La plata que efectivamente entra (o los cheques) es el neto.
  const montoNeto = Math.round((totalDeclarado - retencionesTotal) * 100) / 100;
  const diff = Math.round((sumaCheques + sumaCartera - montoNeto) * 100) / 100;

  // Genera/regenera la tabla repartiendo el NETO (total - retenciones) entre N cheques
  // (el último absorbe el redondeo)
  const generarCheques = () => {
    const n = Math.max(1, Math.min(24, parseInt(chequeCount) || 1));
    // Los cheques propios cubren lo que no cubren los de cartera tildados
    const total = montoNeto - sumaCartera > 0 ? Math.round((montoNeto - sumaCartera) * 100) / 100 : 0;
    const base = total > 0 ? Math.floor((total / n) * 100) / 100 : 0;
    const rows: ChequeRow[] = Array.from({ length: n }, (_, i) => ({
      checkNumber: '',
      bank: cheques[0]?.bank || '',
      amount: String(i === n - 1 ? Math.round((total - base * (n - 1)) * 100) / 100 : base),
      issueDate: cheques[0]?.issueDate || hoy(),
      dueDate: hoy(),
    }));
    setCheques(rows);
  };

  // Editar el primer cheque completa los demás: banco y f. emisión se copian,
  // y si el número es numérico se autonumera correlativo (chequera). Cada fila sigue editable.
  const setCheque = (i: number, patch: Partial<ChequeRow>) => {
    setCheques(prev => prev.map((c, idx) => {
      if (idx === i) return { ...c, ...patch };
      if (i === 0 && idx > 0) {
        const cascade: Partial<ChequeRow> = {};
        if (patch.bank !== undefined) cascade.bank = patch.bank;
        if (patch.issueDate !== undefined) cascade.issueDate = patch.issueDate;
        if (patch.checkNumber !== undefined && /^\d{1,15}$/.test(patch.checkNumber)) {
          cascade.checkNumber = String(Number(patch.checkNumber) + idx).padStart(patch.checkNumber.length, '0');
        }
        if (Object.keys(cascade).length) return { ...c, ...cascade };
      }
      return c;
    }));
  };

  const handleSubmit = async () => {
    if (!accountId) { toast.error('Elegí la cuenta corriente'); return; }
    if (!method) { toast.error('Elegí el método de pago'); return; }
    if (esEdicion && chequesBloqueados.length) {
      toast.error(`No se puede corregir: el cheque ${chequesBloqueados[0]} ya no está en cartera. Corregilo desde Cheques.`);
      return;
    }
    if (esCheque) {
      if (!cheques.length && !carteraSel.length) { toast.error('Generá los cheques o tildá cheques en cartera'); return; }
      for (let i = 0; i < cheques.length; i++) {
        const c = cheques[i];
        if (!(Number(c.amount) > 0)) { toast.error(`Cheque ${i + 1}: monto inválido`); return; }
        if (!c.checkNumber.trim()) { toast.error(`Cheque ${i + 1}: falta el número`); return; }
        if (!c.bank) { toast.error(`Cheque ${i + 1}: falta el banco`); return; }
        if (!c.issueDate) { toast.error(`Cheque ${i + 1}: falta la fecha de emisión`); return; }
        if (!c.dueDate) { toast.error(`Cheque ${i + 1}: falta la fecha de cobro`); return; }
      }
    } else if (!(totalDeclarado > 0)) { toast.error('Ingresá un monto válido'); return; }
    if (retencionesTotal > 0 && montoNeto < 0) { toast.error('Las retenciones no pueden superar el monto total'); return; }

    setSaving(true);
    try {
      const payload: any = {
        // La plata que entra es el neto: el total menos lo retenido
        amount: esCheque ? sumaCheques + sumaCartera : montoNeto,
        paymentMethod: method,
        date: payDate || undefined,
        description: description || undefined,
        linkedPurchase: linkedPurchase || undefined,
      };
      if (esCheque) payload.cheques = cheques.map(c => ({ checkNumber: c.checkNumber.trim(), bank: c.bank, amount: Number(c.amount), issueDate: c.issueDate, dueDate: c.dueDate }));
      // Cheques en cartera tildados: el server los endosa al proveedor
      if (esCheque && carteraSel.length) payload.chequesCartera = carteraSel;
      const retList = RETENCIONES
        .map(r => ({ tipo: r.tipo, amount: Number(retenciones[r.tipo].amount) || 0, certificado: retenciones[r.tipo].certificado.trim() }))
        .filter(r => r.amount > 0);
      if (retList.length) payload.retenciones = retList;
      await axios.post(`/api/current-accounts/${accountId}/payment`, payload);
      // Corrección: el pago viejo (y sus cheques pendientes) se borra recién
      // cuando el nuevo quedó registrado, para no perderlo si algo falla.
      if (esEdicion) {
        await axios.delete(`/api/current-accounts/${accountId}/movements/${editMovementId}`);
      }
      const total = (esCheque ? sumaCheques + sumaCartera : montoNeto) + retencionesTotal;
      const verbo = esEdicion ? 'Pago corregido' : 'Pago registrado';
      toast.success(`${verbo} — ${fmtARS(total)}${retencionesTotal > 0 ? ` (${fmtARS(retencionesTotal)} en retenciones)` : ''}`);
      navigate(esEdicion ? `/current-accounts/${accountId}` : '/current-accounts');
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Error al registrar el pago');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 max-w-6xl mx-auto">
      <div>
        <Link to="/current-accounts" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-1">
          <ArrowLeftIcon className="h-3.5 w-3.5" /> Volver a Ctas. Corrientes
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{esEdicion ? 'Corregir pago' : 'Registrar pago'}</h1>
        <p className="text-sm text-muted-foreground">
          {esEdicion
            ? 'Se reemplaza el pago original: al guardar queda este y el saldo se recalcula.'
            : 'Pago de cuenta corriente. Con cheques podés dividir el total en varios con distintas fechas de cobro.'}
        </p>
      </div>

      {esEdicion && loadingMovement && (
        <p className="text-sm text-muted-foreground">Cargando el pago...</p>
      )}
      {esEdicion && chequesBloqueados.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          Este pago tiene cheques que ya salieron de cartera ({chequesBloqueados.join(', ')}).
          No se puede corregir desde acá porque se duplicarían: resolvelos primero en Cheques.
        </div>
      )}

      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Cuenta corriente *</Label>
              <CtaCombobox accounts={accounts} value={accountId} onChange={setAccountId} />
              {account && (
                <p className="text-xs text-muted-foreground">Saldo actual: <span className="font-medium">{fmtARS(account.currentBalance)}</span></p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Método de pago *</Label>
              <Select value={method} onValueChange={v => { setMethod(v); if (v !== 'cheque') setCheques([]); }}>
                <SelectTrigger><SelectValue placeholder="Elegir método..." /></SelectTrigger>
                <SelectContent>{methods.map(m => <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{esCheque ? 'Monto total a repartir *' : 'Monto *'}</Label>
                {account && account.currentBalance > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs rounded-md"
                    onClick={() => setAmount(String(Math.round(account.currentBalance * 100) / 100))}
                  >
                    Saldo total · {fmtARS(account.currentBalance)}
                  </Button>
                )}
              </div>
              <MoneyInput value={amount} onChange={setAmount} />
            </div>
            <div className="space-y-1.5">
              <Label>Fecha del pago</Label>
              <DatePicker value={payDate} onChange={setPayDate} />
              {esCheque && !pagoEnElFuturo && (
                <p className="text-xs text-muted-foreground">Cuándo se pagó. No es la fecha de los cheques.</p>
              )}
              {pagoEnElFuturo && (
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  Esa fecha todavía no llegó. Poné el día en que se pagó.
                  {' '}
                  <button
                    type="button"
                    className="underline underline-offset-2"
                    onClick={() => setPayDate(hoy())}
                  >
                    Usar hoy
                  </button>
                </p>
              )}
            </div>
            {esCheque && (
              <div className="space-y-1.5">
                <Label>Cantidad de cheques</Label>
                <div className="flex gap-2">
                  <Input type="number" min="1" max="24" value={chequeCount} onChange={e => setChequeCount(e.target.value)} className="w-24" />
                  <Button type="button" variant="outline" onClick={generarCheques} disabled={!(totalDeclarado > 0)}>
                    Generar cheques
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Cheques en cartera para endosar (solo pagos a proveedores) */}
          {esCheque && esProveedor && (
            <div className="rounded-lg border">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Cheques en cartera</div>
                  <div className="text-xs text-muted-foreground">Tildá los que le endosás al proveedor en este pago. El resto lo cubrís con cheques propios.</div>
                </div>
                {carteraSel.length > 0 && (
                  <div className="text-sm font-semibold tabular-nums">{carteraSel.length} · {fmtARS(sumaCartera)}</div>
                )}
              </div>
              {carteraDisp.length === 0 ? (
                <p className="px-3 py-3 text-sm text-muted-foreground">No hay cheques en cartera.</p>
              ) : (
                <div className="max-h-64 divide-y overflow-y-auto">
                  {carteraDisp.map((c: any) => {
                    const on = carteraSel.includes(c.id);
                    return (
                      <label key={c.id} className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm ${on ? 'bg-muted/50' : ''}`}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => setCarteraSel(prev => (on ? prev.filter(id => id !== c.id) : [...prev, c.id]))}
                          className="h-4 w-4 rounded border-input"
                        />
                        <span className="w-28 shrink-0 truncate font-mono">{c.checkNumber || 's/n'}</span>
                        <span className="min-w-0 flex-1 truncate">
                          {c.bank || 'Sin banco'}
                          {c.emitterName || c.clientName ? <span className="text-muted-foreground"> · {c.emitterName || c.clientName}</span> : null}
                        </span>
                        <span className="w-24 shrink-0 text-xs text-muted-foreground">vence {c.dueDate ? String(c.dueDate).slice(0, 10).split('-').reverse().join('/') : 's/f'}</span>
                        <span className="w-28 shrink-0 text-right tabular-nums">{fmtARS(Number(c.amount) || 0)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tabla de cheques */}
          {esCheque && cheques.length > 0 && (
            <div className="rounded-lg border overflow-x-auto">
              <Table className="min-w-[860px]">
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b">
                    <TableHead className="h-9 px-3 text-xs w-8">#</TableHead>
                    <TableHead className="h-9 px-3 text-xs">N° cheque *</TableHead>
                    <TableHead className="h-9 px-3 text-xs w-[200px]">Banco *</TableHead>
                    <TableHead className="h-9 px-3 text-xs w-[140px] text-right">Monto *</TableHead>
                    <TableHead className="h-9 px-3 text-xs w-[150px]">F. emisión *</TableHead>
                    <TableHead className="h-9 px-3 text-xs w-[150px]">F. cobro *</TableHead>
                    <TableHead className="h-9 w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cheques.map((c, i) => (
                    <TableRow key={i} className="border-b hover:bg-muted/20">
                      <TableCell className="px-3 py-1.5 text-xs text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="px-2 py-1.5">
                        <Input value={c.checkNumber} onChange={e => setCheque(i, { checkNumber: e.target.value })} placeholder="12345678" className="h-8 text-sm min-w-[110px]" />
                      </TableCell>
                      <TableCell className="px-2 py-1.5">
                        <BankCombobox value={c.bank} onChange={v => setCheque(i, { bank: v })} placeholder="Banco..." className="h-8 text-xs min-w-[170px]" />
                      </TableCell>
                      <TableCell className="px-2 py-1.5">
                        <MoneyInput value={c.amount} onChange={v => setCheque(i, { amount: v })} className="h-8 text-sm text-right min-w-[130px]" />
                      </TableCell>
                      <TableCell className="px-2 py-1.5">
                        <DatePicker value={c.issueDate} onChange={v => setCheque(i, { issueDate: v })} className="h-8 text-sm w-full min-w-[130px]" />
                      </TableCell>
                      <TableCell className="px-2 py-1.5">
                        <DatePicker value={c.dueDate} onChange={v => setCheque(i, { dueDate: v })} className="h-8 text-sm w-full min-w-[130px]" />
                      </TableCell>
                      <TableCell className="px-1 py-1.5 text-right">
                        {cheques.length > 1 && (
                          <Tip label="Quitar cheque">
                            <Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-red-500" onClick={() => setCheques(prev => prev.filter((_, idx) => idx !== i))}>
                              <TrashIcon className="h-3.5 w-3.5" />
                            </Button>
                          </Tip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/30 font-semibold hover:bg-muted/30">
                    <TableCell colSpan={3} className="px-3 py-2 text-sm text-right">Suma de los cheques:</TableCell>
                    <TableCell className="px-3 py-2 text-sm text-right whitespace-nowrap">{fmtARS(sumaCheques)}</TableCell>
                    <TableCell colSpan={3} className="px-3 py-2">
                      {diff !== 0 && totalDeclarado > 0 && (
                        <Badge variant="outline" className={`text-xs ${diff > 0 ? 'text-amber-600' : 'text-red-500'}`}>
                          {diff > 0 ? `+${fmtARS(diff)} sobre` : `${fmtARS(diff)} bajo`} el total
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
          {esCheque && cheques.length > 0 && (
            <p className="text-xs text-muted-foreground -mt-2">
              El pago se registra por la <strong>suma de los cheques</strong> ({fmtARS(sumaCheques)}) y cada cheque entra a la cartera (Finanzas → Cheques) con su fecha de cobro.
            </p>
          )}

          {/* Retenciones: el cliente paga menos plata pero cancela más deuda */}
          <div className="rounded-lg border border-dashed p-3 space-y-2">
            <Label className="text-sm">Retenciones del cliente (opcional)</Label>
            <p className="text-xs text-muted-foreground -mt-1">
              Si el cliente retuvo impuestos, las retenciones son parte del monto: la plata que entra es el monto menos lo retenido. Cada retención queda como movimiento propio.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {RETENCIONES.map(r => (
                <div key={r.tipo} className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{r.label}</Label>
                  <MoneyInput value={retenciones[r.tipo].amount} onChange={v => setRetencion(r.tipo, { amount: v })} />
                  <Input
                    value={retenciones[r.tipo].certificado}
                    onChange={e => setRetencion(r.tipo, { certificado: e.target.value })}
                    placeholder="N° certificado (opcional)"
                    className="h-8 text-xs"
                  />
                </div>
              ))}
            </div>
            {retencionesTotal > 0 && (
              <p className={`text-xs ${montoNeto < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {montoNeto < 0
                  ? `Las retenciones (${fmtARS(retencionesTotal)}) superan el monto total`
                  : <>Retenciones: <span className="font-medium text-foreground">{fmtARS(retencionesTotal)}</span> · {esCheque ? 'A repartir en cheques' : `Entra por ${methods.find(m => m.code === method)?.name || 'el método elegido'}`}: <span className="font-medium text-foreground">{fmtARS(montoNeto)}</span> · Total que cancela: <span className="font-medium text-foreground">{fmtARS(totalDeclarado)}</span></>}
              </p>
            )}
          </div>

          {account?.entityType === 'provider' && purchases.length > 0 && (
            <div className="space-y-1.5">
              <Label>Compra relacionada (opcional)</Label>
              <Select value={linkedPurchase || 'none'} onValueChange={v => setLinkedPurchase(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Sin vincular" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin vincular</SelectItem>
                  {purchases.map(p => (
                    <SelectItem key={p.id} value={p.purchaseNumber}>
                      {p.purchaseNumber} · {fmtARS(p.total || 0)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Vinculá el pago a una orden de compra del proveedor para tener el seguimiento (queda en el detalle del movimiento).</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Descripción (opcional)</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Detalle del pago..." />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => navigate('/current-accounts')}>Cancelar</Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90"
            >
              {saving ? 'Registrando...' : (() => {
                const total = esCheque ? sumaCheques + retencionesTotal : totalDeclarado;
                return `Registrar pago${total > 0 ? ` (${fmtARS(total)})` : ''}`;
              })()}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
